import { CROP_SLUG, CROP_VARIETY_SLUG, CV_STAGE_SLUG, SITE_SLUG } from './api/dimension_metas';
import { UserData } from './api/users';
import { Trial } from './models/trial';
import { TrialState } from './models/TrialState';
import { session } from './session';

// From backend permission codename.
export const PermissionCodename = {
  VIEW_TRIAL: 'view_trial',
  ADD_TRIAL: 'add_trial',
  CHANGE_TRIAL: 'change_trial',
  DELETE_TRIAL: 'delete_trial',
  ASSIGN_TRIAL_STAFF: 'assign_trial_staff',
  ENTER_TRIAL_OBSERVATIONS: 'enter_trial_observations',

  VIEW_TEMPLATE: 'view_template',
  ADD_TEMPLATE: 'add_template',
  CHANGE_TEMPLATE: 'change_template',
  DELETE_TEMPLATE: 'delete_template',

  VIEW_TRAIT: 'view_measurementmeta',
  ADD_TRAIT: 'add_measurementmeta',
  CHANGE_TRAIT: 'change_measurementmeta',
  DELETE_TRAIT: 'delete_measurementmeta',
  SUGGEST_TRAIT: 'suggest_measurementmeta',

  VIEW_DIMENSION_META: 'view_dimensionmeta',
  ADD_DIMENSION_META: 'add_dimensionmeta',
  CHANGE_DIMENSION_META: 'change_dimensionmeta',
  DELETE_DIMENSION_META: 'delete_dimensionmeta',

  VIEW_DIMENSION: 'view_dimension',
  ADD_DIMENSION: 'add_dimension',
  CHANGE_DIMENSION: 'change_dimension',
  DELETE_DIMENSION: 'delete_dimension',

  VIEW_SCHEDULED_VISIT: 'view_scheduledvisit',
  ADD_SCHEDULED_VISIT: 'add_scheduledvisit',
  CHANGE_SCHEDULED_VISIT: 'change_scheduledvisit',
  DELETE_SCHEDULED_VISIT: 'delete_scheduledvisit',

  VIEW_TRAIT_TAG: 'view_measurementmetatag',
  ADD_TRAIT_TAG: 'add_measurementmetatag',
  CHANGE_TRAIT_TAG: 'change_measurementmetatag',
  DELETE_TRAIT_TAG: 'delete_measurementmetatag',

  VIEW_TRAIT_CATEGORY: 'view_traitcategory',
  ADD_TRAIT_CATEGORY: 'add_traitcategory',
  CHANGE_TRAIT_CATEGORY: 'change_traitcategory',
  DELETE_TRAIT_CATEGORY: 'delete_traitcategory',

  VIEW_DOCUMENT_TAG: 'view_documentcategory',
  ADD_DOCUMENT_TAG: 'add_documentcategory',
  CHANGE_DOCUMENT_TAG: 'change_documentcategory',
  DELETE_DOCUMENT_TAG: 'delete_documentcategory',

  VIEW_COUNTRY: 'view_country',
  ADD_COUNTRY: 'add_country',
  CHANGE_COUNTRY: 'change_country',
  DELETE_COUNTRY: 'delete_country',

  VIEW_REGION: 'view_region',
  ADD_REGION: 'add_region',
  CHANGE_REGION: 'change_region',
  DELETE_REGION: 'delete_region',

  VIEW_AGRO_REGION: 'view_agroregion',
  ADD_AGRO_REGION: 'add_agroregion',
  CHANGE_AGRO_REGION: 'change_agroregion',
  DELETE_AGRO_REGION: 'delete_agroregion',

  VIEW_GLOBAL_REGION: 'view_globalregion',
  ADD_GLOBAL_REGION: 'add_globalregion',
  CHANGE_GLOBAL_REGION: 'change_globalregion',
  DELETE_GLOBAL_REGION: 'delete_globalregion',

  VIEW_SITE: 'view_site',
  ADD_SITE: 'add_site',
  CHANGE_SITE: 'change_site',
  DELETE_SITE: 'delete_site',

  VIEW_CROP: 'view_crop',
  ADD_CROP: 'add_crop',
  CHANGE_CROP: 'change_crop',
  DELETE_CROP: 'delete_crop',

  VIEW_CROP_VARIETY: 'view_cropvariety',
  ADD_CROP_VARIETY: 'add_cropvariety',
  CHANGE_CROP_VARIETY: 'change_cropvariety',
  DELETE_CROP_VARIETY: 'delete_cropvariety',

  VIEW_STAGE: 'view_stage',
  ADD_STAGE: 'add_stage',
  CHANGE_STAGE: 'change_stage',
  DELETE_STAGE: 'delete_stage',

  VIEW_UNIT_GROUP: 'view_unitgroup',
  VIEW_SEASON: 'view_season',
  ADD_SEASON: 'add_season',
  CHANGE_SEASON: 'change_season',
  DELETE_SEASON: 'delete_season',

  VIEW_USER: 'view_user',
  ADD_USER: 'add_user',
  CHANGE_USER: 'change_user',
  DELETE_USER: 'delete_user',
} as const;

type PermissionActor = { id?: string; role: string } | null;

export function canCreateTemplate(userData: PermissionActor) {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.ADD_TEMPLATE);
  }
  return isAtLeastTemplateManager(userData);
}

export function canCreateTrial(isTemplate: boolean): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(
      isTemplate ? PermissionCodename.ADD_TEMPLATE : PermissionCodename.ADD_TRIAL
    );
  }
  if (session.isReadOnlyAdmin()) {
    return false;
  }
  return isTemplate ? session.isAtLeastTemplateEditor() : !session.isRestrictedManager();
}

export function canEditTrial(userData: PermissionActor, trial: Trial) {
  if (customPermissionsEnabled()) {
    const codename = trial.template ? PermissionCodename.CHANGE_TEMPLATE : PermissionCodename.CHANGE_TRIAL;
    return trialStateAllowsEditingAny(trial) && session.hasPermission(codename);
  }
  return trialStateAllowsEditingAny(trial) && trialEditableByUser(userData, trial);
}

export function canToggleTrialArchived(userData: PermissionActor, trial: Trial) {
  if (customPermissionsEnabled()) {
    const codename = trial.template ? PermissionCodename.CHANGE_TEMPLATE : PermissionCodename.CHANGE_TRIAL;
    return session.hasPermission(codename);
  }
  return trialEditableByUser(userData, trial);
}

export function canDeleteTrial(userData: PermissionActor, trial: Trial): boolean {
  if (customPermissionsEnabled()) {
    const codename = trial.template ? PermissionCodename.DELETE_TEMPLATE : PermissionCodename.DELETE_TRIAL;
    return trialStateAllowsEditingAny(trial) && session.hasPermission(codename);
  }
  return trialStateAllowsEditingAny(trial) && trialEditableByUser(userData, trial);
}

// Frozen until unarchived: data, state and definition.
export function isArchiveLocked(trial: Trial) {
  return !trial.template && trial.archived();
}

export function trialStateAllowsEditingAny(trial: Trial) {
  if ((trial.template && trial.state() === TrialState.Approved) || isArchiveLocked(trial)) {
    return false;
  }

  if (
    !trial.template &&
    [TrialState.DataValidated, TrialState.Paused, TrialState.Canceled, TrialState.Completed].includes(
      trial.state()
    )
  ) {
    return false;
  }

  return true;
}

export function canEditTrialLimited(userData: PermissionActor, trial: Trial) {
  // Allows limited editing options for restricted managers.
  return (
    trialStateAllowsEditingAny(trial) &&
    (canEditTrial(userData, trial) || (!trial.template && userData?.role === 'restricted_manager'))
  );
}

export function canReorderTrialLimitTo(userData: PermissionActor, trial: Trial): boolean {
  return canEditTrialLimited(userData, trial) && trial.isDraft();
}

export function canAssignTrialStaff(userData: PermissionActor, trial: Trial): boolean {
  if (customPermissionsEnabled()) {
    return trialStateAllowsEditingAny(trial) && session.hasPermission(PermissionCodename.ASSIGN_TRIAL_STAFF);
  }
  return canEditTrialLimited(userData, trial);
}

export function canEnterObservationsForTrial(userData: PermissionActor, trial: Trial): boolean {
  const allowedState = trial.state() === TrialState.Active && !isArchiveLocked(trial);
  if (customPermissionsEnabled()) {
    return allowedState && canEnterObservations();
  }
  return allowedState && canEditTrialLimited(userData, trial);
}

export function canEnterObservations(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.ENTER_TRIAL_OBSERVATIONS);
  }
  return true;
}

export function canAddSites(userData: PermissionActor, trial: Trial): boolean {
  return canEditTrialLimited(userData, trial);
}

export function canEditTrialLayout(userData: PermissionActor, trial: Trial): boolean {
  return canEditTrialLimited(userData, trial);
}

export function canExportTrial(trial: Trial): boolean {
  return (session.isAtLeastEditor() || session.isReadOnlyAdmin()) && !trial.template && trial.isNotDraft();
}

export function canRememberCustomExportOptions(): boolean {
  return session.isAtLeastEditor();
}

export function allowViewLimitToDimensionNameAndAttributes() {
  return !session.isRestrictedManager();
}

export function canMakeTrialDraft(userData: UserData, trial: Trial): boolean {
  if (isArchiveLocked(trial)) {
    return false;
  }

  const isDraftState = trial.state() === TrialState.Preview || trial.state() === TrialState.Approved;
  if (customPermissionsEnabled()) {
    const codename = trial.template ? PermissionCodename.CHANGE_TEMPLATE : PermissionCodename.CHANGE_TRIAL;
    return isDraftState && session.hasPermission(codename);
  }

  return isDraftState && trialEditableByUser(userData, trial);
}

export interface TrialCopySource {
  isTemplate: boolean;
  hasCrop: boolean;
  isLibrary: boolean;
}

export function canCopyAsTrial(source: TrialCopySource): boolean {
  // A crop-less template can only be copied as a template — a trial requires a crop.
  return canCreateTrial(false) && (!source.isTemplate || source.hasCrop);
}

export function canCopyAsTemplate(userData: PermissionActor, source: TrialCopySource): boolean {
  // A library-mode trial can't be copied to a template: its traits and scheduled visits might
  // not have library references set, and library-mode templates must always have them.
  return canCreateTemplate(userData) && (source.isTemplate || !source.isLibrary);
}

export function canCopyTrial(userData: PermissionActor, trial: Trial): boolean {
  if (customPermissionsEnabled()) {
    const source = {
      isTemplate: trial.template,
      hasCrop: !!trial.crop(),
      isLibrary: trial.editMode === 'library',
    };
    return canCopyAsTrial(source) || canCopyAsTemplate(userData, source);
  }
  return trialEditableByUser(userData, trial);
}

export function trialEditableByUser(userData: PermissionActor, trial: Trial): boolean {
  if (!userData) {
    return false;
  }

  if (userData.role === 'read_only_admin' || (!trial.template && userData.role === 'restricted_manager')) {
    return false;
  }

  if (!trial.template) {
    // otherwise, when it's visible it can be edited
    return true;
  }

  if (userData.role === 'admin') {
    return true;
  }

  if (trial.template && userData.role === 'template_editor') {
    return trial.owners().some((owner) => owner.id === userData.id);
  }

  return false;
}

export function canEditCrop() {
  return session.isFieldtrials();
}

export function canEditMeasurementType(userData: { role: string }) {
  return isAtLeastTemplateManager(userData);
}

export function canEditAttributeChoiceList() {
  // Attribute choice lists are dimension-attribute configuration, so they share the dimension-definition gate.
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.CHANGE_DIMENSION_META);
  }
  return isAtLeastTemplateManager(session.tenant());
}

export function canCreateCropVariety(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.ADD_CROP_VARIETY);
  }
  return session.isFieldtrials() && isAtLeastTemplateManager(session.tenant());
}

export function canEditCropVariety(userData: { role: string } | null) {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.CHANGE_CROP_VARIETY);
  }
  return session.isFieldtrials() && isAtLeastTemplateManager(userData);
}

export function canDeleteCropVariety(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.DELETE_CROP_VARIETY);
  }
  return session.isFieldtrials() && isAtLeastTemplateManager(session.tenant());
}

export function canViewCropVariety(userData: { role: string } | null) {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.VIEW_CROP_VARIETY);
  }
  return session.isFieldtrials() && (isAtLeastTemplateManager(userData) || session.isReadOnlyAdmin());
}

export function canEditBreeder(userData: { role: string }) {
  return canEditCropVariety(userData);
}

export function canViewBreeder(userData: { role: string }) {
  return canEditCropVariety(userData) || session.isReadOnlyAdmin();
}

export function canViewOrganizations() {
  return session.isAtLeastEditor() || session.isReadOnlyAdmin();
}

export function canViewTrialAnalysis() {
  return session.isAtLeastEditor() || session.isReadOnlyAdmin();
}

export function canEditDimension() {
  if (session.tenant()?.admin_only_data_editing_mode && !session.isAdmin()) {
    return false;
  }
  return session.isAtLeastEditor() || session.isRestrictedManager();
}

export function canViewDimensionMeta(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.VIEW_DIMENSION_META);
  }
  // Without custom permissions, viewing isn't checked, user's role determines if it will be shown in the menu.
  return true;
}

export function canCreateDimensionMeta(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.ADD_DIMENSION_META);
  }
  return canEditDimension();
}

export function canEditDimensionMeta(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.CHANGE_DIMENSION_META);
  }
  return canEditDimension();
}

export function canDeleteDimensionMeta(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.DELETE_DIMENSION_META);
  }
  return canEditDimension();
}

export function canViewDimensionRecord(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.VIEW_DIMENSION);
  }
  return true;
}

export function canCreateDimensionRecord(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.ADD_DIMENSION);
  }
  return canEditDimension();
}

export function canEditDimensionRecord(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.CHANGE_DIMENSION);
  }
  return canEditDimension();
}

export function canDeleteDimensionRecord(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.DELETE_DIMENSION);
  }
  return canEditDimension();
}

export function canImportDimensionRecord(): boolean {
  // Import bulk-creates and updates records, so it requires both add and change.
  if (customPermissionsEnabled()) {
    return (
      session.hasPermission(PermissionCodename.ADD_DIMENSION) &&
      session.hasPermission(PermissionCodename.CHANGE_DIMENSION)
    );
  }
  return canEditDimension();
}

export function canEditMMLibrary() {
  if (session.tenant()?.admin_only_data_editing_mode && !session.isAdmin()) {
    return false;
  }
  return session.isAtLeastEditor();
}

export function canViewTrait(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.VIEW_TRAIT);
  }
  return true;
}

export function canCreateTrait(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.ADD_TRAIT);
  }
  return canEditMMLibrary();
}

export function canEditTrait(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.CHANGE_TRAIT);
  }
  return canEditMMLibrary();
}

export function canDeleteTrait(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.DELETE_TRAIT);
  }
  return canEditMMLibrary();
}

export function canViewTraitCategory(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.VIEW_TRAIT_CATEGORY);
  }
  return isAtLeastTemplateManager(session.tenant());
}

export function canCreateTraitCategory(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.ADD_TRAIT_CATEGORY);
  }
  return isAtLeastTemplateManager(session.tenant());
}

export function canEditTraitCategory(userData: { role: string }) {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.CHANGE_TRAIT_CATEGORY);
  }
  return isAtLeastTemplateManager(userData);
}

export function canDeleteTraitCategory(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.DELETE_TRAIT_CATEGORY);
  }
  return isAtLeastTemplateManager(session.tenant());
}

export function canEditTPP() {
  return session.tenant()?.tpp_enabled && session.isAtLeastEditor();
}

export function canViewTPP() {
  return session.tenant()?.tpp_enabled && (session.isAtLeastEditor() || session.isReadOnlyAdmin());
}

export function canEditGroup() {
  return session.isAtLeastEditor() || session.isRestrictedManager();
}

// The legacy tiers that could reach the Users screen before custom permissions existed.
function isLegacyUserManager() {
  return session.isAdmin() || session.isManager() || session.isRestrictedManager() || session.isEditor();
}

// Answers "may open the Users screen", so the flag-on branch checks the read codename.
export function canViewUsers() {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.VIEW_USER);
  }
  return isLegacyUserManager();
}

export function canInviteUsers() {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.ADD_USER);
  }
  return session.isAdmin() || session.isManager() || session.isRestrictedManager();
}

export function canChangeUsers() {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.CHANGE_USER);
  }
  return isLegacyUserManager();
}

export function canRemoveUsers() {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.DELETE_USER);
  }
  return isLegacyUserManager();
}

export function canViewDocumentTags(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.VIEW_DOCUMENT_TAG);
  }
  return !session.isRestrictedManager();
}

export function canCreateDocumentTag(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.ADD_DOCUMENT_TAG);
  }
  return session.isAtLeastEditor() || session.isRestrictedManager();
}

export function canEditDocumentTags() {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.CHANGE_DOCUMENT_TAG);
  }
  return session.isAtLeastEditor() || session.isRestrictedManager();
}

export function canDeleteDocumentTag(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.DELETE_DOCUMENT_TAG);
  }
  return session.isAtLeastEditor() || session.isRestrictedManager();
}

export function canViewScheduledVisit(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.VIEW_SCHEDULED_VISIT);
  }
  return true;
}

export function canCreateScheduledVisit(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.ADD_SCHEDULED_VISIT);
  }
  return session.isAtLeastEditor();
}

export function canEditScheduledVisit(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.CHANGE_SCHEDULED_VISIT);
  }
  return session.isAtLeastEditor();
}

export function canDeleteScheduledVisit(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.DELETE_SCHEDULED_VISIT);
  }
  return session.isAtLeastEditor();
}

export function canViewTraitTag(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.VIEW_TRAIT_TAG);
  }
  return true;
}

export function canCreateTraitTag(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.ADD_TRAIT_TAG);
  }
  return session.isAtLeastEditor();
}

export function canEditTraitTag(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.CHANGE_TRAIT_TAG);
  }
  return session.isAtLeastEditor();
}

export function canDeleteTraitTag(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.DELETE_TRAIT_TAG);
  }
  return session.isAtLeastEditor();
}

export function canViewSite(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.VIEW_SITE);
  }
  return true;
}

export function canCreateSite(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.ADD_SITE);
  }
  return session.isAtLeastEditor() || session.isRestrictedManager();
}

export function canEditSites() {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.CHANGE_SITE);
  }
  return session.isAtLeastEditor() || session.isRestrictedManager();
}

export function canDeleteSite(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.DELETE_SITE);
  }
  return session.isAtLeastEditor() || session.isRestrictedManager();
}

export function canViewCrops(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.VIEW_CROP);
  }
  return true;
}

export function canCreateCrop(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.ADD_CROP);
  }
  return session.isFieldtrials() && session.isAtLeastTemplateEditor();
}

export function canEditCrops() {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.CHANGE_CROP);
  }
  return session.isFieldtrials() && session.isAtLeastTemplateEditor();
}

export function canDeleteCrop(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.DELETE_CROP);
  }
  return session.isFieldtrials() && session.isAtLeastTemplateEditor();
}

export function canViewStage(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.VIEW_STAGE);
  }
  return true;
}

export function canCreateStage(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.ADD_STAGE);
  }
  return canEditDimension();
}

export function canEditStage(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.CHANGE_STAGE);
  }
  return canEditDimension();
}

export function canDeleteStage(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.DELETE_STAGE);
  }
  return canEditDimension();
}

export function canViewSeason(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.VIEW_SEASON);
  }
  return true;
}

export function canCreateSeason(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.ADD_SEASON);
  }
  return session.isAtLeastEditor();
}

export function canEditSeason(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.CHANGE_SEASON);
  }
  return session.isAtLeastEditor();
}

export function canDeleteSeason(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.DELETE_SEASON);
  }
  return session.isAtLeastEditor();
}

export function canEditUnits() {
  return session.isAtLeastEditor();
}

export function canViewAiSettings() {
  return session.isAdmin() && !!session.tenant()?.ai_enabled;
}

export function canViewUnitGroups() {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.VIEW_UNIT_GROUP);
  }
  return true;
}

export function unitGroupsEnabled() {
  return !!session.tenant()?.unit_groups_enabled;
}

// The selector lists the tenant's groups, which needs the view permission.
export function canChooseUnitGroup() {
  return unitGroupsEnabled() && canViewUnitGroups();
}

export function canMergeSyncReview() {
  return session.isAtLeastEditor();
}

export function canEditTrialReport() {
  return session.isAtLeastEditor();
}

export function canEditMarketSegments() {
  return session.isAtLeastEditor();
}

export function canEditRegistrationRegions() {
  return session.isAtLeastEditor();
}

export function canEditRegistrationPhases() {
  return session.isAtLeastEditor();
}

export function canEditRegistrations() {
  return session.isFieldtrials() && session.isAtLeastEditor();
}

function isAtLeastTemplateManager(userData: { role: string } | null): boolean {
  return !!(
    userData &&
    (userData.role === 'admin' ||
      (session.tenant()?.admin_only_data_editing_mode === false && userData.role === 'template_editor'))
  );
}

export function canViewCountry(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.VIEW_COUNTRY);
  }
  return true;
}

export function canCreateCountry() {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.ADD_COUNTRY);
  }
  if (session.tenant()?.admin_only_data_editing_mode && !session.isAdmin()) {
    return false;
  }
  return (session.isFieldtrials() && session.isAtLeastEditor()) || (session.isS2bim() && session.isAdmin());
}

export function canViewRegion(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.VIEW_REGION);
  }
  return true;
}

export function canCreateRegion(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.ADD_REGION);
  }
  return canCreateCountry();
}

export function canEditRegion(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.CHANGE_REGION);
  }
  return canCreateCountry();
}

export function canDeleteRegion(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.DELETE_REGION);
  }
  return canCreateCountry();
}

export function canViewAgroRegion(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.VIEW_AGRO_REGION);
  }
  return true;
}

export function canCreateAgroRegion(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.ADD_AGRO_REGION);
  }
  return canCreateCountry();
}

export function canEditAgroRegion(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.CHANGE_AGRO_REGION);
  }
  return canCreateCountry();
}

export function canDeleteAgroRegion(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.DELETE_AGRO_REGION);
  }
  return canCreateCountry();
}

export function canViewGlobalRegion(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.VIEW_GLOBAL_REGION);
  }
  return true;
}

export function canEditGlobalRegion(): boolean {
  if (customPermissionsEnabled()) {
    return session.hasPermission(PermissionCodename.CHANGE_GLOBAL_REGION);
  }
  return session.isAtLeastEditor();
}

export function canCreatePartner() {
  return session.isAtLeastEditor();
}

export function canCreateCustomer() {
  return session.isAtLeastEditor();
}

export function canViewChangelog(entityType: string) {
  return (
    session.isS2bim() &&
    (entityType !== 'country' || (entityType === 'country' && session.isAtLeastReadOnlyAdmin()))
  );
}

export function reportsEnabled(): boolean {
  const tenant = session.tenant();
  return APP_CONFIG.ENABLE_REPORTS || (!!tenant && ['niab_3', 'test'].indexOf(tenant.slug) >= 0);
}

export function weatherEnabled(): boolean {
  return !!session.tenant()?.weather_enabled;
}

export function advancedWeatherEnabled(): boolean {
  return !!session.tenant()?.advanced_weather_enabled;
}

/** Serves the React list tables instead of the Knockout lists. */
export function reactListTablesEnabled(): boolean {
  return !!session.tenant()?.new_trials_table_enabled;
}

export function customPermissionsEnabled() {
  return !!session.tenant()?.custom_permissions_enabled;
}

export function requireReasonForTrialStateChange() {
  return !!session.tenant()?.require_reason_for_trial_state_change;
}

export function requireReasonForTrialDeletion() {
  return !!session.tenant()?.require_reason_for_trial_deletion;
}

// Deny-only gate: false only when custom permissions are enabled and the user
// lacks the codename. Composes with existing role checks without changing
// behavior for tenants that don't use custom permissions.
export function customPermissionsAllow(codename: string): boolean {
  return !customPermissionsEnabled() || session.hasPermission(codename);
}

// Must match the backend's slug-to-codename mapping: the four system dimension
// types are gated by their own codenames, all other types by the generic
// dimension codename.
function dimensionPermissionTarget(slug: string | null | undefined): string {
  switch (slug) {
    case SITE_SLUG:
      return 'site';
    case CROP_SLUG:
      return 'crop';
    case CROP_VARIETY_SLUG:
      return 'cropvariety';
    case CV_STAGE_SLUG:
      return 'stage';
    default:
      return 'dimension';
  }
}

export function canViewDimensionRecords(slug: string | null | undefined): boolean {
  return customPermissionsAllow('view_' + dimensionPermissionTarget(slug));
}

export function canAddDimensionRecords(slug: string | null | undefined): boolean {
  return customPermissionsAllow('add_' + dimensionPermissionTarget(slug));
}
